// ─────────────────────────────────────────────────────────────────────
//  Prefill du tunnel de paiement depuis l'Espace de gestion de compte.
//
//  Périmètre VOLONTAIREMENT restreint (décision produit) :
//    - L'identité du payeur est TOUJOURS celle du TITULAIRE (selfContact),
//      jamais un membre de la famille (≠ prefill formulaire, qui lui propose
//      le choix d'un contact).
//    - La facturation propose un CHOIX parmi les adresses du profil du titulaire,
//      avec une option « Nouvelle adresse » qui sera enregistrée dans le profil
//      au moment du Valider (via POST /account/me/address).
//
//  Ce module ne fait QUE des transformations pures (profil → valeurs de form,
//  adresse → champs facturation). Aucune dépendance React / réseau ici : le
//  tunnel (components/paiement/index.js) orchestre les appels et l'état.
// ─────────────────────────────────────────────────────────────────────

// Valeur sentinelle du select d'adresses : « saisir une nouvelle adresse ».
export const NEW_ADDRESS = "__new__"

// Champs de facturation pilotés par le select d'adresses (réinitialisés/écrasés
// quand on change d'adresse). Sert aussi à savoir quoi vider sur « Nouvelle adresse ».
const FACTURATION_FIELDS = ["perso", "raison", "siren", "adresse", "postal", "ville", "etat", "pays", "social_id"]

/**
 * Identité (Informations du Contact) depuis le profil du titulaire.
 * Ne renvoie QUE les clés réellement présentes → on n'écrase jamais un champ
 * du form avec `undefined`/`null` (merge propre côté appelant).
 */
export function buildInfoFromProfile(profile = {}) {
    const out = {}
    if (profile.homme === true || profile.homme === false) out.homme = profile.homme
    if (profile.prenom) out.prenom = profile.prenom
    if (profile.nom) out.nom = profile.nom
    if (profile.email) out.email = profile.email
    if (profile.tel) out.tel = profile.tel
    return out
}

/**
 * Champs de facturation depuis UNE adresse du profil (format sanitizeAddress :
 * {id, type:"PERSO"|"PRO", nom, adresse, postal, ville, etat, pays, siren}).
 *
 * Mapping vers les `name=` du form de paiement :
 *   type PRO     → perso:false + raison (= nom) + siren
 *   type PERSO   → perso:true
 *   adresse/postal/ville/etat/pays → idem
 *
 * On renvoie `perso` explicitement (true/false) pour que le Radio.Group suive.
 */
export function buildFacturationFromAddress(addr = {}) {
    if (!addr) return {}
    const isPro = addr.type === "PRO"
    const out = {
        perso: !isPro,
        adresse: addr.adresse || "",
        postal: addr.postal || "",
        ville: addr.ville || "",
        pays: addr.pays || "France",
    }
    if (addr.etat) out.etat = addr.etat
    if (isPro) {
        out.raison = addr.nom || ""
        out.siren = addr.siren || ""
    }
    return out
}

/**
 * Valeurs des champs de facturation à poser pour « Nouvelle adresse » : on garde
 * `perso` tel quel (l'utilisateur choisit), on vide les champs adresse pour qu'il
 * saisisse de zéro. `pays` repart sur le défaut fourni (pays de l'org).
 */
export function blankFacturationFields(defaultPays = "France") {
    return {
        raison: undefined,
        siren: undefined,
        adresse: "",
        postal: "",
        ville: "",
        etat: undefined,
        social_id: undefined,
        pays: defaultPays,
    }
}

/**
 * Construit les options du <Select> d'adresses (1 par adresse du profil + l'entrée
 * « Nouvelle adresse »). Le label est lisible (adresse, ville) avec le type.
 * @returns {Array<{value, label, address}>}
 */
export function buildAddressOptions(profile = {}, labels = {}) {
    const adresses = Array.isArray(profile.adresses) ? profile.adresses : []
    const options = adresses.map((a, idx) => ({
        value: a.id || String(idx),
        label: addressLabel(a, labels),
        address: a,
    }))
    options.push({value: NEW_ADDRESS, label: labels.newAddress || "Nouvelle adresse", address: null})
    return options
}

// Libellé compact d'une adresse pour le select.
// Pour une adresse PRO, le marqueur « (entreprise) » et la raison sociale sont
// placés EN TÊTE (plus lisible dans un select natif tronqué sur mobile).
function addressLabel(a = {}, labels = {}) {
    const parts = []
    if (a.adresse) parts.push(a.adresse)
    const cityLine = [a.postal, a.ville].filter(Boolean).join(" ")
    if (cityLine) parts.push(cityLine)
    let label = parts.join(", ") || (labels.address || "Adresse")
    if (a.type === "PRO") {
        const prefix = (labels.proPrefix || "(entreprise)") + " "
        const raison = a.nom ? a.nom + " · " : ""
        label = prefix + raison + label
    }
    return label
}

/**
 * Construit le payload {address} pour POST /account/me/address à partir des valeurs
 * de form saisies (cas « Nouvelle adresse » → on persiste dans le profil au Valider).
 * Format aligné sur sanitizeAddress côté API.
 */
export function buildAddressPayloadFromForm(data = {}) {
    const isPro = data.perso === false
    const address = {
        type: isPro ? "PRO" : "PERSO",
        nom: isPro ? (data.raison || null) : null,
        adresse: data.adresse || null,
        postal: data.postal || null,
        ville: data.ville || null,
        etat: data.etat || null,
        pays: data.pays || "France",
        siren: isPro ? (data.siren || null) : null,
    }
    return address
}

export {FACTURATION_FIELDS}
